import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME, SAFE_HTTP_METHODS } from '@napaicp/shared'
import type { ApiErrorBody, ApiMeta, ApiSuccess } from '@napaicp/schemas'

/**
 * Empty in development so requests go to `/api` on the same origin and are
 * proxied to the worker by Vite. Set to the worker's origin for deployed
 * frontends that are not served from the same domain.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

/**
 * The platform's own public origin — same build-time source
 * (`VITE_SITE_URL`) that `prerender.mjs` and the Pages Functions under
 * `apps/web/functions/` already use, so a competition's "platform internal
 * path" helper never hardcodes a production host of its own.
 */
export const SITE_URL = (import.meta.env.VITE_SITE_URL ?? 'https://aicp.ntpu.ai').replace(
  /\/+$/,
  '',
)

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown> | undefined

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.error.code
    this.details = body.error.details
  }
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`
  for (const entry of document.cookie.split('; ')) {
    if (entry.startsWith(prefix)) {
      return decodeURIComponent(entry.slice(prefix.length))
    }
  }
  return null
}

export interface ApiRequestOptions {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /**
   * Lets the request outlive the page navigation/unload that triggered it —
   * the standard `fetch` mechanism for "fire this on `pagehide`/unload and
   * don't cancel it", used by `help-search-analytics.ts` (`AC-265`) so a
   * queued analytics flush isn't lost when a `/help` visitor navigates away
   * right after searching. Left `undefined` (native `fetch` default
   * `false`) for every other caller.
   */
  keepalive?: boolean
  /** Extra request headers (e.g. `Idempotency-Key`); never overrides content-type or CSRF. */
  headers?: Record<string, string>
}

/**
 * The single entry point for talking to the API.
 *
 * It never touches `localStorage`: the session lives in an HttpOnly cookie the
 * browser attaches on its own. The only thing this layer adds is the CSRF
 * header, read from the companion cookie the server set at sign-in.
 */
export async function apiRequest<TData>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<TData> {
  const method = options.method ?? 'GET'
  const headers = new Headers(options.headers)

  if (options.body !== undefined) {
    headers.set('content-type', 'application/json')
  }

  if (!SAFE_HTTP_METHODS.includes(method)) {
    const csrfToken = readCookie(CSRF_COOKIE_NAME)
    if (csrfToken !== null) {
      headers.set(CSRF_HEADER_NAME, csrfToken)
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    ...(options.keepalive === undefined ? {} : { keepalive: options.keepalive }),
  })

  const payload: unknown = await response.json()

  if (!response.ok) {
    if (isErrorBody(payload)) {
      throw new ApiError(response.status, payload)
    }
    throw new ApiError(response.status, {
      error: { code: 'INTERNAL_ERROR', message: 'Unexpected response from the server.' },
    })
  }

  return (payload as ApiSuccess<TData>).data
}

/**
 * Same request/error handling as `apiRequest`, but also returns `meta` — for
 * the paginated admin list endpoints that report `total`/`limit`/`offset`
 * alongside `data`, which `apiRequest` otherwise discards.
 */
export async function apiRequestWithMeta<TData>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<{ data: TData; meta: ApiMeta }> {
  const method = options.method ?? 'GET'
  const headers = new Headers()

  if (options.body !== undefined) {
    headers.set('content-type', 'application/json')
  }

  if (!SAFE_HTTP_METHODS.includes(method)) {
    const csrfToken = readCookie(CSRF_COOKIE_NAME)
    if (csrfToken !== null) {
      headers.set(CSRF_HEADER_NAME, csrfToken)
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
    ...(options.keepalive === undefined ? {} : { keepalive: options.keepalive }),
  })

  const payload: unknown = await response.json()

  if (!response.ok) {
    if (isErrorBody(payload)) {
      throw new ApiError(response.status, payload)
    }
    throw new ApiError(response.status, {
      error: { code: 'INTERNAL_ERROR', message: 'Unexpected response from the server.' },
    })
  }

  const success = payload as ApiSuccess<TData>
  return { data: success.data, meta: success.meta }
}

function isErrorBody(payload: unknown): payload is ApiErrorBody {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    'error' in payload &&
    typeof (payload as ApiErrorBody).error?.code === 'string'
  )
}

/** Raw binary upload (e.g. a speaker's PDF) with the same CSRF and error handling as `apiRequest`. */
export async function apiUpload<TData>(
  path: string,
  file: Blob,
  contentType: string,
): Promise<TData> {
  const headers = new Headers({ 'content-type': contentType })
  const csrfToken = readCookie(CSRF_COOKIE_NAME)
  if (csrfToken !== null) headers.set(CSRF_HEADER_NAME, csrfToken)
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers,
    credentials: 'include',
    body: file,
  })
  const payload: unknown = await response.json()
  if (!response.ok) {
    if (isErrorBody(payload)) throw new ApiError(response.status, payload)
    throw new ApiError(response.status, {
      error: { code: 'INTERNAL_ERROR', message: 'Unexpected response from the server.' },
    })
  }
  return (payload as ApiSuccess<TData>).data
}
